import { AppShell, BOOKING_SETTINGS, BrandLogo, TeamHoldBanner, agentInPlan, useHoldStore, useTheme } from '@elva/ui';
import type { SidebarNav, SidebarSubItem } from '@elva/ui';
import {
  LocationWorkspaceSwitcher,
  useLocationStore,
  useWorkspaceLabel,
} from '@elva/platform';
import { useAccountUser } from '@/app/layouts/useAccountUser';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { initAiAssistantBridge } from '@/app/aiAssistantSetup';
import {
  AI_TEAM_CHILD_PATHS,
  AI_TEAM_CHILDREN,
  getAiTeamChildFromPath,
  getNavKeyFromPath,
  NAV_PATHS,
  SIDEBAR_NAV,
} from '@/app/config/nav';

/** The three agents that keep answering even under a whole-team hold. */
const ANSWERING = new Set(['receptionist', 'sms', 'email']);
import { buildPageBreadcrumb } from '@/app/layouts/pageBreadcrumb';
import { PageTopBarProvider, usePageTopBarContext } from '@/app/layouts/pageTopBar';
import { ElvaPageLoader } from '@/components/ElvaPageLoader';
import { useLocationInvalidation } from '@/data/useLocationInvalidation';
import { useLocationScope } from '@/data/useLocationScope';
import { SettingsTopBarClear } from '@/features/settings/SettingsTopBarClear';
import { useBootstrap } from '@/features/workspace/hooks/useBootstrap';
import { useSidebarCounts } from '@/features/workspace/hooks/useSidebarCounts';
import { loadElvaRemote } from '@/lib/loadElvaRemote';

const ElvaFederatedChatHost = lazy(async () => {
  const mod = await import('@/app/aiAssistantSetup');
  return { default: mod.ElvaFederatedChatHost };
});

function AppLayoutShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = getNavKeyFromPath(location.pathname);
  const activeChild = getAiTeamChildFromPath(location.pathname);
  const workspaceLabel = useWorkspaceLabel();
  const { locationId, isLocationReady } = useLocationScope();
  const selectedCoreLocationId = useLocationStore((s) => s.selectedCoreLocationId);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { theme } = useTheme();
  const { breadcrumb: breadcrumbOverride, actionsRef, actionsTick } = usePageTopBarContext();
  const lastActionPathRef = useRef<string | null>(null);

  useLocationInvalidation();
  useBootstrap(locationId ?? null);

  useEffect(() => {
    initAiAssistantBridge();
  }, []);

  const { data: sidebarCounts } = useSidebarCounts(locationId ?? null);

  const sidebarNav = useMemo(() => {
    if (!sidebarCounts) return SIDEBAR_NAV; // no badges while loading
    return SIDEBAR_NAV.map((item) => {
      if (item.key === 'triage' && sidebarCounts.triage_open_count > 0) {
        return { ...item, count: sidebarCounts.triage_open_count };
      }
      if (item.key === 'email' && sidebarCounts.email_unread_count > 0) {
        return { ...item, count: sidebarCounts.email_unread_count };
      }
      if (item.key === 'messages' && sidebarCounts.messages_unread_count > 0) {
        return { ...item, count: sidebarCounts.messages_unread_count };
      }
      return item;
    });
  }, [sidebarCounts]);

  // Live AI-Team dots (spec 36 §7) and Warm transfer hidden without the receptionist (spec 37).
  const holdStore = useHoldStore();
  const hasReceptionist = agentInPlan('receptionist', holdStore.plan);
  // AI SMS and AI Email have no sublist row (spec 36 §7 rev 3). An individual hold
  // lights an amber dot on Messages / Email. A whole-team hold leaves them answering.
  const smsHeld = holdStore.agents.sms?.held === true;
  const emailHeld = holdStore.agents.email?.held === true;
  // Online bookings is shown only with the add-on (spec 50 §3).
  const hasOnlineBooking = BOOKING_SETTINGS.bought;
  const nav: SidebarNav[] = sidebarNav
    .filter(
      (item) =>
        (hasReceptionist || item.key !== 'warm-transfer') &&
        (hasOnlineBooking || item.key !== 'online-bookings'),
    )
    .map((item) => {
      if (item.key === 'ai-team') {
        return {
          ...item,
          children: AI_TEAM_CHILDREN.map((c): SidebarSubItem => {
            const held =
              holdStore.agents[c.key]?.held === true ||
              (holdStore.team.held && !ANSWERING.has(c.key));
            const status: SidebarSubItem['status'] =
              !agentInPlan(c.key, holdStore.plan) ? 'locked' : held ? 'paused' : 'on';
            return { ...c, status };
          }),
        };
      }
      if (item.key === 'messages' && smsHeld) return { ...item, heldDot: true };
      if (item.key === 'email' && emailHeld) return { ...item, heldDot: true };
      return item;
    });

  const accountUser = useAccountUser();

  const usesLegacyHeader = active === 'calendar' || active === 'bird-eye';
  const breadcrumb =
    breadcrumbOverride ?? buildPageBreadcrumb(location.pathname, workspaceLabel);
  // Reset the top-bar actions on every route change so a page never inherits the
  // previous page's actions. The layout renders before the child route, so this
  // clears the slot first; a page that wants actions re-sets them via usePageTopBar
  // (or leaves it empty by doing nothing). One systemic guard instead of a
  // per-page useClearPageTopBarActions call.
  if (lastActionPathRef.current !== location.pathname) {
    actionsRef.current = null;
    lastActionPathRef.current = location.pathname;
  }
  // actionsTick ensures we re-read the ref after the active page registers actions.
  const topBarActions = actionsTick >= 0 ? actionsRef.current : null;

  return (
    <>
      <AppShell
        fill
        sidebarCollapsed={sidebarCollapsed}
        onSidebarCollapsedChange={setSidebarCollapsed}
        sidebar={{
          brand: {
            name: 'Elva',
            badge: 'FrontDesk',
            logo: <BrandLogo src="/logo.svg" alt="Elva" />,
          },
          workspaceSlot: <LocationWorkspaceSwitcher collapsed={sidebarCollapsed} />,
          nav,
          active,
          ...(activeChild !== undefined ? { activeChild } : {}),
          onNav: (key) => {
            const path = NAV_PATHS[key];
            if (path) void navigate(path);
          },
          onNavChild: (key) => {
            const path = AI_TEAM_CHILD_PATHS[key];
            if (path) void navigate(path);
          },
          // The account moved to the top-right (spec 41); the location switcher
          // stays bottom-left via workspaceSlot, so no `user` on the sidebar.
        }}
        {...(usesLegacyHeader
          ? {}
          : {
              topbar: {
                breadcrumb,
                titleGradient: true,
                actions: topBarActions,
                user: accountUser,
              },
            })}
      >
        {location.pathname.startsWith('/settings') ? <SettingsTopBarClear /> : null}
        {/* Amendment 32 §6: the held banner shows on every page while the team is on hold. */}
        <div className="px-[30px] pt-3 empty:hidden"><TeamHoldBanner /></div>
        {isLocationReady ? <Outlet /> : <ElvaPageLoader />}
      </AppShell>
      {selectedCoreLocationId != null ? (
        <Suspense fallback={null}>
          <ElvaFederatedChatHost
            loadRemote={loadElvaRemote}
            locationId={String(selectedCoreLocationId)}
            theme={theme}
          />
        </Suspense>
      ) : null}
    </>
  );
}

export function AppLayout() {
  return (
    <PageTopBarProvider>
      <AppLayoutShell />
    </PageTopBarProvider>
  );
}
